/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { FC } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { getTemplateLabelEnums } from '@/utils/evalTemplates';
import { sendEvalPromptToKahu } from '@/utils/sendEvalPromptToKahu';
import EvalBadge from '@/ux-common/src/components/evals/eval-badge';
import { EvalResponseType, EvalResults } from '@/ux-common/src/types/api';
import { sizeStyles } from '@/ux-common/src/utils/constants';
import { Match, TSequenceData } from 'types/prompt-sequence-view';

import { cn } from '@/lib/utils';
import { useEvals } from '@/hooks/useEvals';

import { truncateTraceId } from '../display-page/traces/traceUtils';
import { Skeleton } from '../ui/skeleton';
import { CustomPromptComponent } from './custom-prompt-component';
import { CustomPromptComponentConversations } from './custom-prompt-component-conversations';

interface DetailSequenceViewProps {
  data: TSequenceData;
  onSplit?: () => void;
  totalTokens?: number;
  completionTokens?: number;
  promptTokens?: number;
  factName?: string;
  appName?: string;
  workflowName?: string;
  searchTerm?: string;
  spanEvals?: EvalResponseType | null;
  globalMatchOffset?: number;
  currentMatchIndex?: number;
  navigableMatches?: Match[];
  filterStartTime?: string | null;
  filterEndTime?: string | null;
}
export const DetailSequenceView: FC<DetailSequenceViewProps> = ({
  data,
  onSplit,
  totalTokens,
  completionTokens,
  promptTokens,
  factName = '',
  appName = '',
  workflowName,
  spanEvals,
  searchTerm,
  globalMatchOffset,
  currentMatchIndex,
  navigableMatches,
  filterStartTime,
  filterEndTime,
}) => {
  const { messages } = useLanguage();
  const promptSequenceConstants = messages?.PromptSequenceView;
  const { inputData, outputData, status, endTime, span_id } = data;

  const { state, evaluateActions } = useEvals();
  const { evalTemplates } = state;
  const { infEvalLoading } = state;
  const spanStartTime = inputData?.[0]?.startTime ?? endTime;

  const matchSpanEvalArr: EvalResults[] =
    spanEvals?.results
      ?.filter((spanEval) => spanEval.fact_id === `${data.trace_id}.${span_id}`)
      ?.filter((spanEval) => spanEval.eval_found === true) ?? [];

  // this calculation depends on the order of navigable array object, if they change, it can break, in future while updating that, please take care of this also.
  function getCurrentMatchIndex(
    navigableMatches: Match[] | undefined,
    type: string,
    span_id: string,
    index: number
  ): number {
    if (!navigableMatches) return -1;

    const currentMatchIndex = navigableMatches.findIndex(
      (cur) =>
        cur.promptIndex === index &&
        cur.promptType === type &&
        cur.span_id === span_id
    );

    if (currentMatchIndex === -1) return -1;

    return currentMatchIndex;
  }

  return (
    <div className="mb-3 flex flex-col gap-2">
      {/* Render all input/output prompts as sequence for conversation and others */}
      {factName === 'traces' ? (
        <>
          {inputData?.map((prompt, index) => (
            <CustomPromptComponent
              key={`in-${index}`}
              data={prompt}
              index={index}
              onSplit={onSplit}
              status={status}
              time={prompt.startTime}
              totalTokens={totalTokens}
              completionTokens={completionTokens}
              promptTokens={promptTokens}
              span_id={span_id}
              type="input"
              searchTerm={searchTerm}
              currentMatchIndex={currentMatchIndex}
              globalMatchOffset={globalMatchOffset}
              matchCounter={getCurrentMatchIndex(
                navigableMatches,
                'input',
                span_id,
                index
              )}
            />
          ))}
          {typeof outputData === 'string' ? (
            <CustomPromptComponent
              data={outputData}
              index={inputData.length}
              onSplit={onSplit}
              status={status}
              time={endTime}
              totalTokens={totalTokens}
              completionTokens={completionTokens}
              promptTokens={promptTokens}
              span_id={span_id}
              type="output"
              searchTerm={searchTerm}
              currentMatchIndex={currentMatchIndex}
              globalMatchOffset={globalMatchOffset}
              matchCounter={getCurrentMatchIndex(
                navigableMatches,
                'output',
                span_id,
                0
              )}
            />
          ) : Array.isArray(outputData) ? (
            outputData?.map((prompt, index) => (
              <CustomPromptComponent
                key={`out-${index}`}
                data={prompt}
                index={inputData.length + index}
                onSplit={onSplit}
                status={status}
                time={endTime}
                totalTokens={totalTokens}
                completionTokens={completionTokens}
                promptTokens={promptTokens}
                span_id={span_id}
                type="output"
                searchTerm={searchTerm}
                currentMatchIndex={currentMatchIndex}
                globalMatchOffset={globalMatchOffset}
                matchCounter={getCurrentMatchIndex(
                  navigableMatches,
                  'output',
                  span_id,
                  index
                )}
              />
            ))
          ) : (
            promptSequenceConstants?.NotFound?.output_not_found
          )}
        </>
      ) : (
        <div className="mx-4">
          <div className="flex items-center gap-2 rounded-tl-md rounded-tr-md  border border-[#0A2D3A7A] bg-[#0A343C] p-2">
            <span className="text-body-sm text-body-subtle">
              {promptSequenceConstants?.General?.spanId}:{' '}
              {truncateTraceId(data.span_id)}
            </span>
            <span className="h-1 w-1 rounded-full bg-[#40565A]"></span>
            <span className="text-body-sm text-form-input-disabled">
              {data.span_type}
            </span>
            <span className="h-1 w-1 rounded-full bg-[#40565A]"></span>
            {infEvalLoading ? (
              <Skeleton
                className={cn(
                  'animate-pulse-custom w-16 rounded-full bg-[#8C9EA1]',
                  sizeStyles['compact']
                )}
              />
            ) : matchSpanEvalArr && matchSpanEvalArr.length > 0 ? (
              matchSpanEvalArr?.map((matchSpanEval) => (
                <EvalBadge
                  key={`${matchSpanEval.fact_id}_${matchSpanEval.eval_timestamp}`}
                  size="compact"
                  evalObj={matchSpanEval}
                  submitManualLabel={evaluateActions?.submitManualLabel}
                  createEvalsJobs={evaluateActions?.createEvalsJobs}
                  factName="inferences"
                  startTime={spanStartTime}
                  endTime={endTime}
                  appName={appName}
                  workflowName={matchSpanEval.workflow_name ?? workflowName}
                  templateEnums={getTemplateLabelEnums(
                    evalTemplates,
                    matchSpanEval.eval_name
                  )}
                  templateLoading={state.templateLoading}
                  sendEvalPromptToKahu={sendEvalPromptToKahu}
                  filterStartTime={filterStartTime}
                  filterEndTime={filterEndTime}
                />
              ))
            ) : null}
          </div>
          <div className="flex flex-col gap-2.5 rounded-bl-xl rounded-br-xl border border-line-tertiary bg-[#0000000F] px-3 pb-3 pt-2">
            {inputData?.map((prompt, index) => (
              <CustomPromptComponentConversations
                key={`in-${index}`}
                data={prompt}
                index={index}
                isFirstInput={index === 0}
                onSplit={onSplit}
                status={status}
                time={prompt.startTime}
                totalTokens={totalTokens}
                completionTokens={completionTokens}
                promptTokens={promptTokens}
                conversationType="Input"
                span_id={span_id}
              />
            ))}
            {typeof outputData === 'string' ? (
              <CustomPromptComponentConversations
                data={outputData}
                index={inputData.length}
                isFirstInput={true}
                onSplit={onSplit}
                status={status}
                time={endTime}
                totalTokens={totalTokens}
                completionTokens={completionTokens}
                promptTokens={promptTokens}
                conversationType="Output"
                span_id={span_id}
              />
            ) : Array.isArray(outputData) ? (
              outputData?.map((prompt, index) => (
                <CustomPromptComponentConversations
                  key={`out-${index}`}
                  data={prompt}
                  isFirstInput={index === 0}
                  index={inputData.length + index}
                  onSplit={onSplit}
                  status={status}
                  time={endTime}
                  totalTokens={totalTokens}
                  completionTokens={completionTokens}
                  promptTokens={promptTokens}
                  conversationType="Output"
                  span_id={span_id}
                />
              ))
            ) : (
              promptSequenceConstants?.NotFound?.output_not_found
            )}
          </div>
        </div>
      )}
    </div>
  );
};
